import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatMoney, formatPercent } from '../../utils/formatNumbers'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined'
import {
  selectAverageActionsForRelatedControls,
  selectAverageCostForRelatedControls,
  selectCountDoneActionsForRelatedControls,
  selectCountOtherActionsForRelatedControls,
  selectInProgressActionsForRelatedControls,
  selectLateRateForRelatedControls,
  selectRelatedControlsActionsCosts,
  selectRelatedControlsCosts,
  selectSecurityControl,
} from '../../redux/securityControls/securityControlsSelector'
import {
  fetchAverageActionsForRelatedControls,
  fetchAverageCostForRelatedControls,
  fetchInProgressActionsForRelatedControls,
  fetchLateRateForRelatedControls,
  fetchRelatedControlsActionsCosts,
  fetchRelatedControlsCosts,
  getSecurityControlById,
  fetchCountOtherActionsForRelatedControls,
  fetchCountDoneActionsForRelatedControls,
} from '../../redux/securityControls/securityControlsAction'
import { AppDispatch, useAppDispatch } from '../../redux/store'
import { selectSpecifications } from '../../redux/specification/specificationSelectors'
import { formatNormLabel } from '../../utils/normLabels'
import MatrixComponent from '../../components/common/MatrixTable'
import {
  DetailAccordion,
  DetailButton,
  DetailKpiCard,
  DetailKpiGrid,
  DetailPageHead,
} from '../../components/common/detail'
import { AnalysisPageLoader } from '../../components/common/analysisLayout'
import {
  AnalysisPdfButton,
  useAnalysisPdfExport,
} from '../../components/common/AnalysisPdfButton'
import AnalysisPdfReport, { AnalysisPdfKpi } from '../../Pdf/AnalysisPdfReport'
import { formatTitle } from '../../utils/formatTitle'
import { writeSecurityNav } from '../../utils/securityNav'
import '../securityFramework/SecurityFramework.css'
import '../../components/common/maturity-analysis.css'
import './MappingFlow.css'

function RelatedControlsAnalysis() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const dispatch: AppDispatch = useAppDispatch()
  const selectedNorm = useSelector(selectSpecifications)[0]?.NormBase || ''
  const currency = useSelector(selectSpecifications)[0]?.Currency
  const lateRateData = useSelector(selectLateRateForRelatedControls)
  const relatedControlsCosts = useSelector(selectRelatedControlsCosts)
  const { selectedRelated, id } = useParams<{
    selectedRelated: string
    id: string
  }>()
  const securityControl = useSelector(selectSecurityControl)
  const averageActionsForRelatedControls = useSelector(
    selectAverageActionsForRelatedControls
  )
  const inProgressActions = useSelector(
    selectInProgressActionsForRelatedControls
  )
  const averageCost = useSelector(selectAverageCostForRelatedControls)
  const costData = useSelector(selectRelatedControlsActionsCosts)
  const countOtherActions = useSelector(
    selectCountOtherActionsForRelatedControls
  )
  const countDoneActions = useSelector(selectCountDoneActionsForRelatedControls)
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState({
    inProgress: true,
    costs: true,
  })

  const formattedData = inProgressActions.map((item) => ({
    [t('tables.relatedActionsInProgress.Label')]: item.Label,
    [t('tables.relatedActionsInProgress.InProgressActionsCount')]:
      item.InProgressActionsCount,
    SecurityControlId: item.SecurityControlId,
  }))
  const formattedDataActionsCost =
    relatedControlsCosts?.Details?.map((item) => ({
      [t('tables.relatedActionsCost.Label')]: item.Label,
      [t('tables.relatedActionsCost.ActionsCount')]: item.ActionsCount,
      [t('tables.relatedActionsCost.TotalCost')]: item.TotalCost,
    })) || []

  const headers = [
    t('tables.relatedActionsInProgress.Label'),
    t('tables.relatedActionsInProgress.InProgressActionsCount'),
  ]
  const headersActionsCost = [
    t('tables.relatedActionsCost.Label'),
    t('tables.relatedActionsCost.ActionsCount'),
    t('tables.relatedActionsCost.TotalCost'),
  ]

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (!id) {
        setLoading(false)
        return
      }
      setLoading(true)
      try {
        await Promise.all([
          dispatch(getSecurityControlById(id)),
          dispatch(fetchAverageActionsForRelatedControls(id)),
          dispatch(fetchAverageCostForRelatedControls(id)),
          dispatch(fetchRelatedControlsActionsCosts(id)),
          dispatch(fetchLateRateForRelatedControls(id)),
          dispatch(fetchInProgressActionsForRelatedControls(id)),
          dispatch(fetchRelatedControlsCosts(id)),
          dispatch(fetchCountOtherActionsForRelatedControls(id)),
          dispatch(fetchCountDoneActionsForRelatedControls(id)),
        ])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [dispatch, id])

  useEffect(() => {
    if (!id || !securityControl?.Label) return
    writeSecurityNav({
      norm: selectedRelated || selectedNorm,
      controlId: id,
      controlLabel: formatTitle(securityControl.Label),
      familyId: securityControl.SecurityCheckFamilyId,
    })
  }, [
    id,
    securityControl?.Label,
    securityControl?.SecurityCheckFamilyId,
    selectedRelated,
    selectedNorm,
  ])

  const data = averageActionsForRelatedControls?.data
  const metric = (value: number | string | null | undefined) =>
    value === undefined || value === null ? '—' : value
  const percent = (value: number | undefined) =>
    value === undefined ? '—' : formatPercent(value)
  const money = (value: number | string | undefined) =>
    value === undefined ? '—' : formatMoney(value, currency)

  const backPath =
    selectedRelated && id
      ? `/relatedDataMapped/${selectedRelated}/${id}`
      : '/securityFramework'
  const controlTitle =
    formatTitle((securityControl?.Label as string) || '') ||
    t('relatedControlsAnalysisPage.title')

  const pdf = useAnalysisPdfExport()
  const computedOn = new Date().toLocaleDateString(i18n.language, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
  const pdfKpi = (
    label: string,
    value: number | string | null | undefined,
    display: string
  ): AnalysisPdfKpi => ({
    label,
    value: display,
    tone: Number(value) > 0 ? 'teal' : 'muted',
  })
  const pdfKpis = [
    pdfKpi(
      t('relatedControlsAnalysisPage.cards.relatedControlsCount'),
      data?.RelatedControlsCount,
      String(metric(data?.RelatedControlsCount))
    ),
    pdfKpi(
      t('relatedControlsAnalysisPage.cards.totalActions'),
      data?.TotalActions,
      String(metric(data?.TotalActions))
    ),
    pdfKpi(
      t('relatedControlsAnalysisPage.cards.averageActionsForRelatedControls'),
      data?.AverageActionsForRelatedControls,
      percent(data?.AverageActionsForRelatedControls)
    ),
    pdfKpi(
      t('relatedControlsAnalysisPage.cards.averageCostForRelatedControls'),
      averageCost?.AverageCostPerAction,
      money(averageCost?.AverageCostPerAction)
    ),
    pdfKpi(
      t('relatedControlsAnalysisPage.cards.totalCostForRelatedControlsActions'),
      costData?.TotalRelatedActionsCost,
      money(costData?.TotalRelatedActionsCost)
    ),
    pdfKpi(
      t('relatedControlsAnalysisPage.cards.lateRateForRelatedControls'),
      lateRateData?.LateRate,
      percent(lateRateData?.LateRate)
    ),
    pdfKpi(
      t('relatedControlsAnalysisPage.cards.lateActions'),
      lateRateData?.LateActions,
      String(metric(lateRateData?.LateActions))
    ),
    pdfKpi(
      t('relatedControlsAnalysisPage.cards.otherActions'),
      countOtherActions?.OtherActionsCount,
      String(metric(countOtherActions?.OtherActionsCount))
    ),
    pdfKpi(
      t('relatedControlsAnalysisPage.cards.doneActions'),
      countDoneActions?.DoneActionsCount,
      String(metric(countDoneActions?.DoneActionsCount))
    ),
  ]

  return (
    <div className="detail-page mapping-flow">
      <DetailPageHead
        eyebrow={t('frameworkMappingPage.analysisEyebrow')}
        title={controlTitle}
        subtitle={t('frameworkMappingPage.analysisSubtitle')}
        actions={
          <>
            <DetailButton variant="ghost" onClick={() => navigate(backPath)}>
              <ArrowBackOutlined sx={{ fontSize: 16 }} />
              {t('frameworkMappingPage.backToMapping')}
            </DetailButton>
            <AnalysisPdfButton
              exporting={pdf.exporting}
              disabled={loading}
              onExport={() =>
                pdf.exportPdf(controlTitle || 'related-controls-analysis')
              }
            />
          </>
        }
      />

      {loading ? (
        <AnalysisPageLoader kpiCount={3} chartCount={1} />
      ) : (
        <>
      <div className="mapping-flow-callout">
        <span className="mapping-flow-step">✓</span>
        <div>
          <strong>
            {t('frameworkMappingPage.analysisGuidance', {
              defaultValue: 'Interpret the mapping',
            })}
          </strong>
          <p>
            {t('frameworkMappingPage.analysisInstruction', {
              defaultValue:
                'Review the indicators first, then expand the tables to identify controls driving active work and cost.',
            })}
          </p>
        </div>
      </div>

      <div className="fw-source-bar">
        <div className="fw-stat">
          <div className="fw-stat-label">
            {t('frameworkMappingPage.sourceFramework', {
              defaultValue: 'Source framework',
            })}
          </div>
          <div className="fw-stat-value">
            {formatNormLabel(selectedRelated || '')}
          </div>
        </div>
        <div className="fw-stat-divider" />
        <div className="fw-stat">
          <div className="fw-stat-label">
            {t('frameworkMappingPage.targetFramework', {
              defaultValue: 'Target framework',
            })}
          </div>
          <div className="fw-stat-value">{formatNormLabel(selectedNorm)}</div>
        </div>
      </div>

      <DetailKpiGrid columns={3}>
        <DetailKpiCard
          label={t('relatedControlsAnalysisPage.cards.relatedControlsCount')}
          value={metric(data?.RelatedControlsCount)}
        />
        <DetailKpiCard
          label={t('relatedControlsAnalysisPage.cards.totalActions')}
          value={metric(data?.TotalActions)}
        />
        <DetailKpiCard
          label={t(
            'relatedControlsAnalysisPage.cards.averageActionsForRelatedControls'
          )}
          value={percent(data?.AverageActionsForRelatedControls)}
        />
        <DetailKpiCard
          label={t(
            'relatedControlsAnalysisPage.cards.averageCostForRelatedControls'
          )}
          value={money(averageCost?.AverageCostPerAction)}
        />
        <DetailKpiCard
          label={t(
            'relatedControlsAnalysisPage.cards.totalCostForRelatedControlsActions'
          )}
          value={money(costData?.TotalRelatedActionsCost)}
        />
        <DetailKpiCard
          label={t('relatedControlsAnalysisPage.cards.lateRateForRelatedControls')}
          value={percent(lateRateData?.LateRate)}
        />
        <DetailKpiCard
          label={t('relatedControlsAnalysisPage.cards.lateActions')}
          value={metric(lateRateData?.LateActions)}
        />
        <DetailKpiCard
          label={t('relatedControlsAnalysisPage.cards.otherActions')}
          value={metric(countOtherActions?.OtherActionsCount)}
        />
        <DetailKpiCard
          label={t('relatedControlsAnalysisPage.cards.doneActions')}
          value={metric(countDoneActions?.DoneActionsCount)}
        />
      </DetailKpiGrid>

      <DetailAccordion
        title={t('tables.relatedActionsInProgress.title')}
        count={formattedData.length}
        open={expanded.inProgress}
        onToggle={() =>
          setExpanded((current) => ({
            ...current,
            inProgress: !current.inProgress,
          }))
        }
      >
        <div className="mapping-matrix-section">
          <MatrixComponent
            variant="analysis"
            title={t('inProgressActionsForRelatedControls')}
            data={formattedData}
            headers={headers}
          />
        </div>
      </DetailAccordion>

      <DetailAccordion
        title={t('tables.relatedActionsCost.title')}
        count={formattedDataActionsCost.length}
        open={expanded.costs}
        onToggle={() =>
          setExpanded((current) => ({
            ...current,
            costs: !current.costs,
          }))
        }
      >
        <div className="mapping-matrix-section">
          <MatrixComponent
            variant="analysis"
            title={t('RelatedActionsCost')}
            data={formattedDataActionsCost}
            headers={headersActionsCost}
          />
        </div>
      </DetailAccordion>
        </>
      )}

      <div className="ma-pdf-print-host" aria-hidden>
        <AnalysisPdfReport
          ref={pdf.ref}
          eyebrow={t('frameworkMappingPage.analysisEyebrow')}
          title={controlTitle}
          subtitle={`${formatNormLabel(selectedRelated || '')} → ${formatNormLabel(selectedNorm)} · ${t('frameworkMappingPage.analysisSubtitle')}`}
          footerTitle={t('relatedControlsAnalysisPage.title')}
          computedOn={computedOn}
          kpis={pdfKpis}
          tables={[
            {
              label: t('tables.relatedActionsInProgress.title'),
              title: t('tables.relatedActionsInProgress.title'),
              headers,
              rows: inProgressActions.map((item) => [
                String(item.Label ?? ''),
                String(item.InProgressActionsCount ?? ''),
              ]),
              numericLast: true,
            },
            {
              label: t('tables.relatedActionsCost.title'),
              title: t('tables.relatedActionsCost.title'),
              headers: headersActionsCost,
              rows: (relatedControlsCosts?.Details || []).map((item) => [
                String(item.Label ?? ''),
                String(item.ActionsCount ?? ''),
                money(item.TotalCost),
              ]),
              numericLast: true,
            },
          ]}
        />
      </div>
    </div>
  )
}

export default RelatedControlsAnalysis
